// Format a raw ECW progress note for readable display.
//
// ECW exports progress notes as a single text blob with section headers
// embedded inline (no line breaks). This module inserts line breaks before
// known section headers so a human reader can scan the note.

// Major sections — get extra spacing + bold treatment
const MAJOR_SECTIONS = [
  'Subjective',
  'Objective',
  'Chief Complaints?',
  'HPI',
  'History of Present Illness',
  'Review of Systems',
  'ROS',
  'Transition of Care',
  'Past Psychiatric History',
  'Past Medical History',
  'Past Surgical History',
  'Family Psychiatric and Social History',
  'Family History',
  'Social History',
  'Allergies',
  'Medications',
  'Current Medications',
  'Vital Signs',
  'Physical Exam',
  'Mental Status Examination',
  'Risk Assessment',
  'Assessment and Diagnostic Impression',
  'Assessment',
  'Diagnoses',
  'DSM 5 Diagnoses',
  'ICD[- ]10 Diagnoses',
  'Provisional Diagnosis',
  'Differential Diagnoses',
  'Psychoeducation Provided',
  'Psychoeducation',
  'Plan',
  'Treatment Plan',
  'Follow[- ]up',
  'CPT',
  'Total Time',
];

// Minor labels — get a newline but no extra spacing
const MINOR_LABELS = [
  // Patient demographic header
  'Patient',
  'Account Number',
  'Provider',
  'DOB',
  'Age',
  'Sex',
  'Date',
  'Phone',
  'Address',
  // Clinical subsections
  'Sources of Information',
  'Setting',
  'Duration',
  'Family Structure',
  'Family Psychiatric History',
  'Stressors',
  'Substance Use',
  'Appearance',
  'Behavior',
  'Speech',
  'Mood',
  'Affect',
  'Thought Process',
  'Thought Content',
  'Cognition',
  'Insight and Judgment',
  'Impulse Control',
  'Suicidal Ideation',
  'Homicidal Ideation',
  'Safety Concerns',
  'Protective Factors',
  'Diagnosis',
  'Prior Diagnoses',
  'Hospitalizations',
  'Therapy',
  'Medication',
  'Referral',
  'Education',
  'Electronic signature of Provider',
  'Sign off status',
  'Generated for',
];

// Build regex that matches any of these labels followed by ':'
// Capture the label so we can rebuild with a newline.
function buildRegex(labels) {
  // Sort longest first so "History of Present Illness" matches before "History"
  const sorted = [...labels].sort((a, b) => b.length - a.length);
  // Use lookbehind-like approach: match " LABEL:" (label preceded by any char)
  // but capture the label
  const pattern = `(${sorted.join('|')})\\s*:`;
  return new RegExp(pattern, 'g');
}

const MAJOR_REGEX = buildRegex(MAJOR_SECTIONS);
const MINOR_REGEX = buildRegex(MINOR_LABELS);

// Visit time and type are header fields, recognised only at the START of a line
// and only before the first major section. EMR notes arrive as one unbroken
// line, so "Visit Type:", "Date/Time:" or "Total Time:" in them never split;
// a note that puts "Time:" / "Type:" on their own lines gets them as labels.
const HEADER_ONLY_REGEX = /(^|\n)[ \t]*(Time|Type)\s*:/g;

/**
 * Format a raw progress note string for display.
 * Returns the same string with line breaks inserted before known sections.
 */
export function formatProgressNote(raw) {
  if (!raw || typeof raw !== 'string') return '';

  let formatted = raw;

  // Strip the redundant page header — everything up to and including
  // "Progress Note" (e.g. "POWELL, Zyon DOB: 03/09/2016 Progress Note ").
  // This avoids duplicate DOB/Age matches from the header banner.
  const progressNoteMarker = 'Progress Note ';
  const markerIdx = formatted.indexOf(progressNoteMarker);
  if (markerIdx !== -1) {
    formatted = formatted.substring(markerIdx + progressNoteMarker.length);
  }

  // Insert double newline + marker before major sections
  formatted = formatted.replace(MAJOR_REGEX, (match, label) => {
    return `\n\n<<MAJOR>>${label}:`;
  });

  // Insert single newline before minor labels
  formatted = formatted.replace(MINOR_REGEX, (match, label) => {
    return `\n<<MINOR>>${label}:`;
  });

  const firstMajor = formatted.indexOf('<<MAJOR>>');
  const head = firstMajor === -1 ? formatted : formatted.slice(0, firstMajor);
  const rest = firstMajor === -1 ? '' : formatted.slice(firstMajor);
  formatted = head.replace(HEADER_ONLY_REGEX, (match, lead, label) => `\n<<MINOR>>${label}:`) + rest;

  return formatted.trim();
}

// Labels that belong in the signature/trailer block (after the main note)
const FOOTER_LABELS = new Set([
  'Electronic signature of Provider',
  'Sign off status',
  'Generated for',
]);

/**
 * Parse a formatted note into structured sections for JSX rendering.
 * Returns an array of { type, zone, label?, body } where:
 *   type: 'major' | 'minor' | 'intro'
 *   zone: 'header' | 'body' | 'footer'
 *
 * Zones are determined by position:
 *   - 'header' until the first MAJOR section
 *   - 'body' until a FOOTER_LABEL is encountered
 *   - 'footer' after that
 */
export function parseFormattedNote(raw) {
  const formatted = formatProgressNote(raw);
  if (!formatted) return [];

  // \n* rather than \n\n?: the trim above leaves the FIRST marker with no
  // newline in front, which used to fold the note's first label into an
  // unlabelled intro that the viewer then hid.
  const parts = formatted.split(/\n*<<(MAJOR|MINOR)>>/);

  const sections = [];
  if (parts[0]?.trim()) {
    sections.push({ type: 'intro', zone: 'header', body: parts[0].trim() });
  }

  let zone = 'header';
  let footerStarted = false;

  for (let i = 1; i < parts.length; i += 2) {
    const type = parts[i].toLowerCase();
    const content = parts[i + 1] || '';
    const colonIdx = content.indexOf(':');
    const label = colonIdx === -1 ? content.trim() : content.slice(0, colonIdx).trim();
    const body = colonIdx === -1 ? '' : content.slice(colonIdx + 1).trim();

    // Transition from header → body on first MAJOR section
    if (zone === 'header' && type === 'major') {
      zone = 'body';
    }
    // Transition into footer when we see a trailer label
    if (!footerStarted && FOOTER_LABELS.has(label)) {
      zone = 'footer';
      footerStarted = true;
    }
    // Once in footer, remaining sections stay in footer
    if (footerStarted) {
      zone = 'footer';
    }

    sections.push({ type, zone, label, body });
  }

  return sections;
}
